import { ComponentEntity, Entity, GroupEntity, UserEntity } from '@backstage/catalog-model';
import {
  buildOrganizationGroupRows,
  buildOrganizationGroupTree,
  isInvisibleOrgGroup,
  OrganizationGroupNode,
  ROOT_ORGANIZATION_NAME,
} from './buildOrganizationGroupRows';
import { buildMembersByGroup, parseManagerUserName, TeamMember, userDisplayName } from './buildOrganizationMembership';
import {
  buildGroupIndex,
  parseOwnerGroupName,
  projectKeysEqual,
  readJiraProjectKeys,
  resolveInheritedProjectKeys,
  resolveInheritedProjectKeysFromParent,
} from './organizationBoardResolution';

export type BoardDisplayMode = 'declared' | 'inherited' | 'override' | 'none';

export type InheritanceDiagramNode = {
  id: string;
  label: string;
  subtitle?: string;
  nodeKind: 'group' | 'component' | 'member' | 'bucket';
  groupType?: string;
  memberRole?: 'manager' | 'member';
  boardMode: BoardDisplayMode;
  projectKeys: string[];
  inheritedFromLabel?: string;
  catalogPath?: string;
  managerLabel?: string;
  children: InheritanceDiagramNode[];
};

export type InheritanceDiagramModel = {
  officialRoot?: InheritanceDiagramNode;
  unofficialRoot?: InheritanceDiagramNode;
};

function catalogPathForEntity(entity: Entity): string {
  const namespace = entity.metadata.namespace ?? 'default';
  const kind = entity.kind.toLowerCase();
  return `/catalog/${namespace}/${kind}/${entity.metadata.name}`;
}

function collectOfficialGroupNames(root: OrganizationGroupNode): Set<string> {
  const names = new Set<string>();
  const visit = (node: OrganizationGroupNode) => {
    names.add(node.name);
    node.children.forEach(visit);
  };
  visit(root);
  return names;
}

function groupDisplayName(entity: Entity | undefined, fallback: string): string {
  if (!entity || entity.kind !== 'Group') {
    return fallback;
  }
  const group = entity as GroupEntity;
  return group.spec.profile?.displayName ?? group.metadata.title ?? fallback;
}

function groupBoardPresentation(
  groupName: string,
  groupIndex: ReturnType<typeof buildGroupIndex>,
  groupEntitiesByName: Map<string, Entity>,
): Pick<InheritanceDiagramNode, 'boardMode' | 'projectKeys' | 'inheritedFromLabel'> {
  const readGroupKeys = (name: string) => {
    const entity = groupEntitiesByName.get(name);
    return entity ? readJiraProjectKeys(entity) : [];
  };

  const groupEntity = groupEntitiesByName.get(groupName);
  const declaredKeys = groupEntity ? readJiraProjectKeys(groupEntity) : [];
  const parentInherited = resolveInheritedProjectKeysFromParent(
    groupName,
    groupIndex,
    readGroupKeys,
  );

  if (
    declaredKeys.length > 0 &&
    !projectKeysEqual(declaredKeys, parentInherited.projectKeys)
  ) {
    return {
      boardMode: 'declared',
      projectKeys: declaredKeys,
    };
  }

  const effectiveKeys =
    declaredKeys.length > 0 ? declaredKeys : parentInherited.projectKeys;
  if (effectiveKeys.length === 0) {
    return {
      boardMode: 'none',
      projectKeys: [],
    };
  }

  const sourceGroup = parentInherited.inheritedFromName
    ? groupEntitiesByName.get(parentInherited.inheritedFromName)
    : undefined;

  return {
    boardMode: 'inherited',
    projectKeys: effectiveKeys,
    inheritedFromLabel: groupDisplayName(sourceGroup, parentInherited.inheritedFromName ?? ''),
  };
}

function componentDiagramNode(
  entity: ComponentEntity,
  groupIndex: ReturnType<typeof buildGroupIndex>,
  groupEntitiesByName: Map<string, Entity>,
): InheritanceDiagramNode {
  const namespace = entity.metadata.namespace ?? 'default';
  const readGroupKeys = (name: string) => {
    const groupEntity = groupEntitiesByName.get(name);
    return groupEntity ? readJiraProjectKeys(groupEntity) : [];
  };
  const explicitKeys = readJiraProjectKeys(entity);
  const ownerGroupName = parseOwnerGroupName(entity.spec?.owner);
  const ownerInherited = ownerGroupName
    ? resolveInheritedProjectKeys(ownerGroupName, groupIndex, readGroupKeys)
    : { projectKeys: [] as string[] };

  let boardMode: BoardDisplayMode = 'none';
  let inheritedFromLabel: string | undefined;
  let projectKeys: string[] = [];

  if (
    explicitKeys.length > 0 &&
    !projectKeysEqual(explicitKeys, ownerInherited.projectKeys)
  ) {
    boardMode = 'override';
    projectKeys = explicitKeys;
  } else if (ownerInherited.projectKeys.length > 0 || explicitKeys.length > 0) {
    boardMode = 'inherited';
    projectKeys =
      explicitKeys.length > 0 ? explicitKeys : ownerInherited.projectKeys;
    const sourceGroup = ownerInherited.inheritedFromName
      ? groupEntitiesByName.get(ownerInherited.inheritedFromName)
      : undefined;
    inheritedFromLabel = groupDisplayName(
      sourceGroup,
      ownerInherited.inheritedFromName ?? '',
    );
  }

  return {
    id: `component:${namespace}/${entity.metadata.name}`,
    label: entity.metadata.name,
    subtitle: entity.metadata.title,
    nodeKind: 'component',
    boardMode,
    projectKeys,
    inheritedFromLabel,
    catalogPath: catalogPathForEntity(entity),
    children: [],
  };
}

function memberDiagramNode(member: TeamMember): InheritanceDiagramNode {
  return {
    id: member.entityRef,
    label: member.displayName,
    subtitle: member.name,
    nodeKind: 'member',
    memberRole: member.role,
    boardMode: 'none',
    projectKeys: [],
    catalogPath: member.catalogPath,
    children: [],
  };
}

function buildGroupDiagramNode(
  groupNode: OrganizationGroupNode,
  componentsByOwner: Map<string, ComponentEntity[]>,
  membersByGroup: Map<string, TeamMember[]>,
  groupIndex: ReturnType<typeof buildGroupIndex>,
  groupEntitiesByName: Map<string, Entity>,
  usersByName: Map<string, UserEntity>,
): InheritanceDiagramNode {
  const board = groupBoardPresentation(groupNode.name, groupIndex, groupEntitiesByName);
  const groupEntity = groupEntitiesByName.get(groupNode.name);
  const managerName = groupEntity
    ? parseManagerUserName((groupEntity.spec as { manager?: string }).manager)
    : undefined;
  const managerUser = managerName ? usersByName.get(managerName) : undefined;
  const managerLabel = managerUser
    ? userDisplayName(managerUser)
    : managerName?.replace(/\./g, ' ');
  const ownedComponents = (componentsByOwner.get(groupNode.name) ?? [])
    .sort((a, b) => a.metadata.name.localeCompare(b.metadata.name))
    .map(component =>
      componentDiagramNode(component, groupIndex, groupEntitiesByName),
    );
  const ownedMembers = (membersByGroup.get(groupNode.name) ?? []).map(memberDiagramNode);

  return {
    id: groupNode.entityRef,
    label: groupNode.displayName,
    subtitle: groupNode.name,
    nodeKind: 'group',
    groupType: groupNode.type,
    boardMode: board.boardMode,
    projectKeys: board.projectKeys,
    inheritedFromLabel: board.inheritedFromLabel,
    catalogPath: groupNode.catalogPath,
    managerLabel,
    children: [
      ...groupNode.children.map(child =>
        buildGroupDiagramNode(
          child,
          componentsByOwner,
          membersByGroup,
          groupIndex,
          groupEntitiesByName,
          usersByName,
        ),
      ),
      ...ownedComponents,
      ...ownedMembers,
    ],
  };
}

function unofficialOwnerRank(ownerLabel: string): number {
  // Pin the generic buckets ("Unassigned owner", "Unsupported owner: …") ahead
  // of named owners so ordering is stable and readable.
  return ownerLabel === 'Unassigned owner' || ownerLabel.startsWith('Unsupported owner:')
    ? 0
    : 1;
}

function unofficialOwnerLabel(ownerGroupName: string | undefined, ownerRaw: unknown): string {
  if (!ownerGroupName) {
    if (typeof ownerRaw === 'string' && ownerRaw.trim()) {
      return `Unsupported owner: ${ownerRaw.trim()}`;
    }
    return 'Unassigned owner';
  }
  return ownerGroupName;
}

function buildUnofficialRoot(
  components: ComponentEntity[],
  groupIndex: ReturnType<typeof buildGroupIndex>,
  groupEntitiesByName: Map<string, Entity>,
): InheritanceDiagramNode | undefined {
  if (components.length === 0) {
    return undefined;
  }

  const componentsByOwner = new Map<string, ComponentEntity[]>();
  for (const component of components) {
    const ownerGroupName = parseOwnerGroupName(component.spec?.owner);
    const ownerLabel = unofficialOwnerLabel(ownerGroupName, component.spec?.owner);
    const bucket = componentsByOwner.get(ownerLabel) ?? [];
    bucket.push(component);
    componentsByOwner.set(ownerLabel, bucket);
  }

  return {
    id: 'bucket:other-owners',
    label: 'Other owners',
    nodeKind: 'bucket',
    boardMode: 'none',
    projectKeys: [],
    children: Array.from(componentsByOwner.entries())
      .sort(([a], [b]) => unofficialOwnerRank(a) - unofficialOwnerRank(b) || a.localeCompare(b))
      .map(([ownerLabel, ownerComponents]) => ({
        id: `owner-bucket:${ownerLabel}`,
        label: ownerLabel,
        nodeKind: 'bucket' as const,
        boardMode: 'none' as const,
        projectKeys: [],
        children: ownerComponents
          .sort((a, b) => a.metadata.name.localeCompare(b.metadata.name))
          .map(component => componentDiagramNode(component, groupIndex, groupEntitiesByName)),
      })),
  };
}

export function buildOrganizationInheritanceDiagram(
  groupEntities: GroupEntity[],
  componentEntities: ComponentEntity[],
  userEntities: UserEntity[] = [],
): InheritanceDiagramModel {
  const membersByGroup = buildMembersByGroup(userEntities, groupEntities);
  const usersByName = new Map(userEntities.map(user => [user.metadata.name, user]));
  const officialGroupEntities = groupEntities.filter(isInvisibleOrgGroup);
  const groupRows = buildOrganizationGroupTree(buildOrganizationGroupRows(officialGroupEntities));
  const officialRootRow = groupRows.find(node => node.name === ROOT_ORGANIZATION_NAME);
  if (!officialRootRow) {
    const unofficialRoot = buildUnofficialRoot(
      componentEntities,
      buildGroupIndex(groupEntities),
      new Map(groupEntities.map(entity => [entity.metadata.name, entity])),
    );
    return { unofficialRoot };
  }

  const officialGroupIndex = buildGroupIndex(officialGroupEntities);
  const officialGroupEntitiesByName = new Map(
    officialGroupEntities.map(entity => [entity.metadata.name, entity]),
  );
  const fullGroupIndex = buildGroupIndex(groupEntities);
  const fullGroupEntitiesByName = new Map(groupEntities.map(entity => [entity.metadata.name, entity]));
  const officialGroupNames = collectOfficialGroupNames(officialRootRow);

  const componentsByOwner = new Map<string, ComponentEntity[]>();
  const unofficialComponents: ComponentEntity[] = [];

  for (const component of componentEntities) {
    const ownerGroupName = parseOwnerGroupName(component.spec?.owner);
    if (ownerGroupName && officialGroupNames.has(ownerGroupName)) {
      const bucket = componentsByOwner.get(ownerGroupName) ?? [];
      bucket.push(component);
      componentsByOwner.set(ownerGroupName, bucket);
      continue;
    }

    unofficialComponents.push(component);
  }

  const officialRoot = buildGroupDiagramNode(
    officialRootRow,
    componentsByOwner,
    membersByGroup,
    officialGroupIndex,
    officialGroupEntitiesByName,
    usersByName,
  );

  const unofficialRoot = buildUnofficialRoot(
    unofficialComponents,
    fullGroupIndex,
    fullGroupEntitiesByName,
  );

  return {
    officialRoot,
    unofficialRoot,
  };
}
